if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // ── Configuratie ────────────────────────────────────────────────
      static get TRIGGER_HANDLES() {
        return ['actyv-pro', 'actyv-pro-incline'];
      }
      static get GIFT_HANDLES() {
        return ['telefoonhouder', 'bekerhouder', 'dempende-mat'];
      }

      get shopRoot() {
        return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      }

      cartFetch(url, payload) {
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        }).then((r) => r.json());
      }

      getProductHandle() {
        const info = this.closest('product-info') || document.querySelector('product-info');
        const url = (info && info.dataset && info.dataset.url) || window.location.pathname;
        const match = url.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : null;
      }

      isTriggerProduct() {
        const handle = this.getProductHandle();
        return handle ? ProductForm.TRIGGER_HANDLES.includes(handle) : false;
      }

      async getCart() {
        const res = await fetch(`${this.shopRoot}cart.js`);
        return res.json();
      }

      // Regels verwijderen op unieke line-key (verandert geen andere regels).
      async removeLines(keys) {
        for (const key of keys) {
          try {
            await this.cartFetch(`${routes.cart_change_url}`, { id: key, quantity: 0 });
          } catch (e) {
            console.error('Regel verwijderen mislukt:', key, e);
          }
        }
      }

      async resolveGiftItems() {
        const items = [];
        for (const handle of ProductForm.GIFT_HANDLES) {
          try {
            const res = await fetch(`${this.shopRoot}products/${handle}.js`);
            if (!res.ok) continue;
            const product = await res.json();
            const variant = product.variants.find((v) => v.available) || product.variants[0];
            if (variant) items.push({ id: variant.id, quantity: 1, properties: { _gift: 'true' } });
          } catch (e) {
            console.error('Kon gratis product niet ophalen:', handle, e);
          }
        }
        return items;
      }

      buildMainItem(formData) {
        const item = {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity') || 1),
        };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = Number(sellingPlan);

        const properties = {};
        for (const [key, value] of formData.entries()) {
          const m = key.match(/^properties\[(.+)\]$/);
          if (m) properties[m[1]] = value;
        }
        if (Object.keys(properties).length) item.properties = properties;
        return item;
      }

      // Altijd renderen vanuit de echte serverstatus (volledige cart).
      async renderFromServer() {
        if (!this.cart) return;
        const ids = this.cart.getSectionsToRender().map((s) => s.id);
        const res = await fetch(`${routes.cart_url}?sections=${ids.join(',')}`);
        const sections = await res.json();
        CartPerformance.measure('add:paint-updated-sections', () => {
          this.cart.renderContents({ sections });
        });
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        if (this.cart) this.cart.setActiveElement(document.activeElement);

        const formData = new FormData(this.form);
        const mainItem = this.buildMainItem(formData);
        const isTrigger = this.isTriggerProduct();
        this.error = false;

        try {
          // 1) Loopband toevoegen (valideren dat het kan).
          const addResp = await this.cartFetch(`${routes.cart_add_url}`, { items: [mainItem] });

          if (addResp.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: mainItem.id,
              errors: addResp.errors || addResp.description,
              message: addResp.message,
            });
            this.handleErrorMessage(addResp.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }
            this.error = true;
            return;
          }

          // 2) Trigger? Groep schoon opnieuw opbouwen, loopband ALS LAATSTE → bovenaan.
          if (isTrigger && this.cart) {
            try {
              const cart = await this.getCart();

              let mainQty = 0;
              const removeKeys = [];
              cart.items.forEach((i) => {
                const isGift = i.properties && i.properties._gift === 'true';
                if (isGift) {
                  removeKeys.push(i.key);
                } else if (i.id === mainItem.id || i.variant_id === mainItem.id) {
                  mainQty += i.quantity;
                  removeKeys.push(i.key);
                }
              });

              await this.removeLines(removeKeys);

              const gifts = await this.resolveGiftItems();
              const mainFinal = {
                id: mainItem.id,
                quantity: mainQty || mainItem.quantity,
                ...(mainItem.properties ? { properties: mainItem.properties } : {}),
                ...(mainItem.selling_plan ? { selling_plan: mainItem.selling_plan } : {}),
              };

              // Cadeaus eerst, loopband laatst. (Cadeaus omgekeerd zodat ze in
              // configuratie-volgorde onder de loopband verschijnen.)
              const items = [...gifts.reverse(), mainFinal];
              const finalResp = await this.cartFetch(`${routes.cart_add_url}`, { items });

              // Vangnet: lukt de heropbouw niet, zet dan minstens de loopband terug.
              if (finalResp.status) {
                await this.cartFetch(`${routes.cart_add_url}`, {
                  items: [{ id: mainItem.id, quantity: mainQty || mainItem.quantity }],
                });
              }
            } catch (e) {
              console.error('Cadeau-groep opbouwen mislukt (loopband staat wel in cart):', e);
            }
          }

          // 3) Geen drawer → naar cart-pagina.
          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          // 4) Renderen vanuit serverstatus + subscribers informeren.
          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          const finalCart = await this.getCart();
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: mainItem.id,
            cartData: finalCart,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });

          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => this.renderFromServer());
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            await this.renderFromServer();
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');

          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}